import { recordServerTime, serverNow } from '@/lib/time/serverClock'
import { jwtSubject } from './jwtSubject'

/**
 * The browser's Supabase access token, minted by our server
 * (GET /api/auth/token). The refresh token never reaches JavaScript: it lives
 * in an HttpOnly cookie and only the server refreshes it. This module only
 * borrows the current access token and asks for a new one when it is close
 * to expiry – judged on the server clock (src/lib/time/serverClock.ts), so a
 * wrong device clock cannot cause refresh loops.
 *
 * State lives on globalThis.__CONFY_AUTH__, seeded by an inline script in the
 * (main) layout before any app code runs (authBootScript):
 *   { token, expiresAt }  – signed in; no request until the token is due
 *   { anonymous: true }   – signed out; never requests until markSignedIn()
 *   undefined             – unknown (embed/other layouts); fetch on first use
 *
 * Token route answers: 200 → new token; 401 → signed out; 503 or a network
 * failure → keep the current token (the 401-retry in authRetryFetch covers
 * an expired one) and back off before asking again (30 s, doubling to 5 min),
 * so an outage costs one request per window, not one per query. A transient
 * failure never signs anyone out.
 */
export type TokenUser = {
  id: string
  email?: string
  user_metadata?: Record<string, unknown>
  app_metadata?: Record<string, unknown>
}
type AuthState = { token: string; expiresAt: number } | { anonymous: true }
export type AuthTransition = 'signed-out' | 'signed-in'

declare global {
  // eslint-disable-next-line no-var
  var __CONFY_AUTH__: AuthState | undefined
}

const REFRESH_MARGIN_MS = 60_000
const BACKOFF_START_MS = 30_000
const BACKOFF_MAX_MS = 5 * 60_000
const CHANNEL_NAME = 'confy-auth'

const listeners = new Set<(e: AuthTransition) => void>()
const tokenListeners = new Set<(token: string) => void>()
let backoffUntil = 0
let backoffMs = BACKOFF_START_MS
let inflight: Promise<string | null> | null = null
let lastUser: TokenUser | null = null
/** What the last token request established (see refreshTokenOutcome). */
let lastOutcome: 'recovered' | 'rejected' | 'unavailable' = 'unavailable'

// Cross-tab sync: a sign-in or sign-out in one tab reaches the others.
const channel =
  typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel(CHANNEL_NAME)
    : null
channel?.addEventListener('message', (event: MessageEvent) => {
  if (event.data === 'signed-out') {
    globalThis.__CONFY_AUTH__ = { anonymous: true }
    lastUser = null
    emit('signed-out')
  } else if (event.data === 'signed-in') {
    emit('signed-in')
  }
})

function emit(e: AuthTransition) {
  listeners.forEach((listener) => listener(e))
}

function currentToken(): string | null {
  const state = globalThis.__CONFY_AUTH__
  return state && 'token' in state ? state.token : null
}

async function fetchToken(): Promise<string | null> {
  try {
    const res = await fetch('/api/auth/token', { credentials: 'same-origin', cache: 'no-store' })
    if (res.status === 401) {
      const wasSignedIn = currentToken() !== null
      lastOutcome = 'rejected'
      globalThis.__CONFY_AUTH__ = { anonymous: true }
      lastUser = null
      if (wasSignedIn) emit('signed-out')
      return null
    }
    if (!res.ok) return unavailable()
    const body = await res.json()
    if (typeof body.server_time === 'number') recordServerTime(body.server_time)
    const previous = currentToken()
    globalThis.__CONFY_AUTH__ = { token: body.access_token, expiresAt: body.expires_at }
    lastUser = body.user ?? null
    lastOutcome = 'recovered'
    backoffUntil = 0
    backoffMs = BACKOFF_START_MS
    if (body.access_token !== previous) tokenListeners.forEach((listener) => listener(body.access_token))
    return body.access_token
  } catch {
    return unavailable()
  }
}

function unavailable(): string | null {
  lastOutcome = 'unavailable'
  backoffUntil = Date.now() + backoffMs
  backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS)
  return currentToken()
}

function sharedFetch(): Promise<string | null> {
  return inflight ?? (inflight = fetchToken().finally(() => { inflight = null }))
}

export async function getAccessToken(opts: { forceRefresh?: boolean } = {}): Promise<string | null> {
  if (typeof window === 'undefined') return null
  const state = globalThis.__CONFY_AUTH__
  if (!state) return sharedFetch()
  if ('anonymous' in state) return null
  if (!opts.forceRefresh && state.expiresAt * 1000 - serverNow() > REFRESH_MARGIN_MS) return state.token
  if (Date.now() < backoffUntil) return state.token
  return sharedFetch()
}

function isAnonymous(): boolean {
  const state = globalThis.__CONFY_AUTH__
  return !!state && 'anonymous' in state
}

/**
 * Force a token request and report what it established: 'recovered' (a valid
 * session), 'rejected' (signed out) or 'unavailable' (no verdict – transient).
 * Used by session recovery (src/lib/auth/sessionRecovery.ts).
 */
export async function refreshTokenOutcome(): Promise<'recovered' | 'rejected' | 'unavailable'> {
  if (typeof window === 'undefined') return 'unavailable'
  if (isAnonymous()) return 'rejected'
  if (Date.now() < backoffUntil) return 'unavailable'
  await sharedFetch()
  return lastOutcome
}

/** The signed-in user from a fresh token round trip (embed pages have no
 * server snapshot of the user). */
export async function fetchTokenUser(): Promise<TokenUser | null> {
  if (typeof window === 'undefined' || isAnonymous()) return null
  await sharedFetch()
  return lastUser
}

/** Called with every new access token (the realtime connection needs it). */
export function onTokenChange(cb: (token: string) => void): () => void {
  tokenListeners.add(cb)
  return () => {
    tokenListeners.delete(cb)
  }
}

export function onAuthTransition(cb: (e: AuthTransition) => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/** After a sign-in flow set the session cookies: allow token requests again
 * and tell other tabs. */
export function markSignedIn(): void {
  const state = globalThis.__CONFY_AUTH__
  if (state && 'anonymous' in state) globalThis.__CONFY_AUTH__ = undefined
  channel?.postMessage('signed-in')
}

/** After sign-out: drop the token and tell other tabs. */
export function markSignedOut(): void {
  globalThis.__CONFY_AUTH__ = { anonymous: true }
  lastUser = null
  channel?.postMessage('signed-out')
}

type BootAuth = { token: string; expiresAt: number } | null | undefined

/**
 * What the server render knows: a token (signed in), null (signed out), or
 * undefined (unknown – a transient auth failure, or a user without a usable
 * session cookie). Unknown must never boot as signed out: the state is left
 * unset so the first query asks the token route.
 */
export function decideBootAuth(input: {
  userId: string | null
  transient: boolean
  session: { access_token?: string; expires_at?: number } | null
}): BootAuth {
  const { userId, transient, session } = input
  if (!userId) return transient ? undefined : null
  if (session?.access_token && session.expires_at && jwtSubject(session.access_token) === userId) {
    return { token: session.access_token, expiresAt: session.expires_at }
  }
  return undefined
}

/** Inline <head> script that seeds the state before any app code runs. */
export function authBootScript(data: BootAuth): string {
  if (data === undefined) return ''
  const value: AuthState = data ? { token: data.token, expiresAt: data.expiresAt } : { anonymous: true }
  return `globalThis.__CONFY_AUTH__=${JSON.stringify(value).replace(/</g, '\\u003c')};`
}
